import { useMemo, useState } from 'react'
import { NetworkStatus } from '@apollo/client'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { useLocation } from 'react-router-dom'

import { ListTerrotoriesSortOrder, TimeUnit } from 'generated/graphql'
import { useTerritoryQueries } from 'hooks-queries/territory'
import { PanelStore } from 'stores'
import { analyticsTracker } from 'utils'
import type { INormalizedDataForTerritoriesTable } from 'utils/getNormalizedTerritoriesDataForTerritoriesTable'
import { getNormalizedTerritoriesDataForTerritoriesTable } from 'utils/getNormalizedTerritoriesDataForTerritoriesTable'

import { GET_FAVORITE_TERRITORIES_LIST } from './global-topic-favorite-list.consts'
import type { TGlobalTopicFavoriteList } from './global-topic-favorite-list.types'
import { DEFAULT_SORT, DEFAULT_TIME, PAGINATION_LIMIT } from '../common/global-topic.consts'
import type { TTableOrder } from '../common/global-topic.types'
import { handleSort } from '../common/global-topic.utils'

export const useGlobalTopicFavoriteListModel = (): TGlobalTopicFavoriteList => {
  const [order, setOrder] = useState<TTableOrder>(DEFAULT_SORT)
  const location = useLocation()

  const workspaceId = (PanelStore?.selectedPanel as any)?.id

  const variables = useMemo(() => {
    const searchParams = new URLSearchParams(location.search)
    const dateRangeValue = searchParams.get('dateRange')
    const timeRange = {
      timeUnit: TimeUnit.Month,
      timeValue: Number(dateRangeValue) || DEFAULT_TIME,
    }

    return {
      pagination: {
        take: PAGINATION_LIMIT,
      },
      filters: {
        name: null,
      },
      timeRange,
      sort: order,
      audience: {},
    }
  }, [location.search, order])

  const { data, error, fetchMore, networkStatus, refetch } = useTerritoryQueries({
    errorLog: GET_FAVORITE_TERRITORIES_LIST,
    queryName: 'getFavoriteTerritories',
    queryVariables: variables,
  })

  const totalCount = data?.listFollowedTerritories?.totalCount
  const hasMore = data?.listFollowedTerritories?.data?.length < data?.listFollowedTerritories?.totalCount
  const isLoading = [NetworkStatus.loading, NetworkStatus.refetch, NetworkStatus.setVariables].includes(networkStatus)
  const isFetchingMore = networkStatus === NetworkStatus.fetchMore

  const tableData: INormalizedDataForTerritoriesTable[] = useMemo(() => {
    const entities = data?.listFollowedTerritories?.data ?? []

    const normalizedTerritoriesDataForTable = getNormalizedTerritoriesDataForTerritoriesTable(
      entities,
      variables?.timeRange?.timeValue,
      workspaceId,
      undefined,
      undefined
    )

    return normalizedTerritoriesDataForTable
  }, [data?.listFollowedTerritories?.data, variables?.timeRange?.timeValue, workspaceId])

  const loadMore = async () => {
    if (isLoading || !hasMore || !data?.listFollowedTerritories?.cursor) return

    const fetchMoreVariables = {
      pagination: {
        take: variables.pagination.take,
        cursor: data?.listFollowedTerritories?.cursor,
      },
    }
    try {
      await fetchMore({
        variables: fetchMoreVariables,
        updateQuery: (prev, { fetchMoreResult }) => {
          const result = fetchMoreResult.listFollowedTerritories?.data?.length

          if (!result) {
            return prev
          }

          return Object.assign({}, prev, {
            listFollowedTerritories: {
              ...prev.listFollowedTerritories,
              cursor: fetchMoreResult?.listFollowedTerritories?.cursor,
              data: [
                ...(prev?.listFollowedTerritories?.data || []),
                ...(fetchMoreResult?.listFollowedTerritories?.data || []),
              ],
            },
          })
        },
      })
    } catch {
      // Errors are reported centrally by the Apollo ErrorLink.
    }
  }

  const handleSetOrder = field => {
    setOrder(prevState => {
      const previousOrder = prevState.order === 'desc' ? ListTerrotoriesSortOrder.Asc : ListTerrotoriesSortOrder.Desc
      const newOrder = {
        field,
        order: prevState.field === field ? previousOrder : ListTerrotoriesSortOrder.Desc,
      }

      const { SORT } = analyticsTracker.eventNames
      analyticsTracker.eventTrack(SORT, {
        Page: 'Search Topics Page',
        'Sort field': field,
        'Sort order': newOrder.order,
      })

      return newOrder
    })
  }

  const [sentryRef] = useInfiniteScroll({
    loading: isLoading || isFetchingMore,
    hasNextPage: hasMore,
    onLoadMore: loadMore,
    disabled: !!error,
  })

  const sortedData = useMemo(() => {
    return handleSort(tableData, order)
  }, [tableData, order])

  return {
    data: sortedData,
    error: Boolean(error),
    isLoading,
    refetch,
    hasMore,
    totalCount,
    isFetchingMore,
    handleSetOrder,
    order,
    sentryRef,
  }
}
